'use client';
import { useState } from 'react';
import Link from 'next/link';
import { industries, agentTypes } from '@/lib/seo-database';
interface AgentMatch {
  slug: string;
  name: string;
  matchScore: number;
  reason: string;
  canDo: string[];
  cannotDo: string[];
}
const CHALLENGE_TO_AGENTS: Record<string, { slugs: string[]; reason: string }> = {
  'missed-calls': { slugs: ['ai-receptionist', 'ai-customer-support'], reason: 'Handles incoming enquiries 24/7 so you never miss a potential customer' },
  'slow-responses': { slugs: ['ai-customer-support', 'ai-receptionist'], reason: 'Provides instant responses to routine questions while your team handles complex ones' },
  'booking-management': { slugs: ['ai-booking-agent', 'ai-receptionist'], reason: 'Automates appointment scheduling, rescheduling, and reminders' },
  'lead-generation': { slugs: ['ai-lead-generation', 'ai-sales-agent'], reason: 'Qualifies and captures leads around the clock, even outside business hours' },
  'social-media': { slugs: ['ai-social-media', 'ai-review-manager'], reason: 'Manages social presence and monitors your online reputation consistently' },
  'review-management': { slugs: ['ai-review-manager', 'ai-customer-support'], reason: 'Monitors reviews across platforms and helps you respond promptly' },
  'invoicing': { slugs: ['ai-invoice-assistant', 'ai-email-marketing'], reason: 'Automates invoice creation, sending, and follow-up on late payments' },
  'email-overload': { slugs: ['ai-email-marketing', 'ai-customer-support'], reason: 'Triages, categorises, and drafts responses to routine emails' },
  'onboarding': { slugs: ['ai-onboarding', 'ai-customer-support'], reason: 'Guides new customers through setup with consistent, thorough onboarding' },
  'data-analysis': { slugs: ['ai-data-analyst'], reason: 'Surfaces insights from your business data without needing a dedicated analyst' },
  'hr-admin': { slugs: ['ai-hr-assistant', 'ai-email-marketing'], reason: 'Handles routine HR queries and processes, freeing your team for strategic work' },
};
const AGENT_CAPABILITIES: Record<string, { canDo: string[]; cannotDo: string[] }> = {
  'ai-receptionist': {
    canDo: ['Answer common questions instantly', 'Route calls to the right person', 'Work 24/7 including holidays', 'Handle multiple enquiries simultaneously'],
    cannotDo: ['Replace a human for sensitive or emotional conversations', 'Make judgement calls on complex situations', 'Guarantee 100% accuracy on every response'],
  },
  'ai-customer-support': {
    canDo: ['Resolve routine support tickets', 'Provide consistent answers from your knowledge base', 'Escalate complex issues to humans', 'Track and log all interactions'],
    cannotDo: ['Handle deeply emotional or escalated complaints alone', 'Override business policies', 'Replace human empathy in sensitive situations'],
  },
  'ai-booking-agent': {
    canDo: ['Schedule and reschedule appointments', 'Send reminders automatically', 'Handle cancellations', 'Manage availability in real-time'],
    cannotDo: ['Make clinical or professional judgements about appointment priority', 'Handle emergencies', 'Replace human scheduling for complex multi-party meetings'],
  },
  'ai-lead-generation': {
    canDo: ['Qualify leads based on criteria you define', 'Capture contact details 24/7', 'Score and prioritise leads', 'Follow up automatically'],
    cannotDo: ['Close complex sales that need relationship building', 'Replace human sales skills for high-value deals', 'Guarantee lead quality'],
  },
  'ai-sales-agent': {
    canDo: ['Engage prospects with personalised messaging', 'Follow up on leads automatically', 'Qualify opportunities 24/7', 'Provide product information instantly'],
    cannotDo: ['Close complex enterprise deals alone', 'Build deep personal relationships', 'Replace human negotiation skills'],
  },
  'ai-social-media': {
    canDo: ['Monitor social channels 24/7', 'Draft responses to comments and messages', 'Schedule content posting', 'Track brand mentions'],
    cannotDo: ['Create original creative content from scratch', 'Handle PR crises alone', 'Replace human creativity for campaigns'],
  },
  'ai-review-manager': {
    canDo: ['Monitor reviews across platforms', 'Draft professional responses', 'Alert you to negative reviews instantly', 'Track review trends'],
    cannotDo: ['Remove or dispute fake reviews on your behalf', 'Guarantee improved ratings', 'Replace genuine customer service improvements'],
  },
  'ai-invoice-assistant': {
    canDo: ['Generate and send invoices automatically', 'Chase late payments with reminders', 'Track payment status', 'Reconcile basic accounts'],
    cannotDo: ['Replace a qualified accountant', 'Handle complex tax calculations', 'Make financial decisions on your behalf'],
  },
  'ai-email-marketing': {
    canDo: ['Triage and categorise incoming emails', 'Draft responses to routine enquiries', 'Follow up automatically', 'Sort by priority'],
    cannotDo: ['Handle highly sensitive correspondence alone', 'Replace human judgement on important emails', 'Guarantee spam-free delivery'],
  },
  'ai-onboarding': {
    canDo: ['Guide new customers step-by-step', 'Answer setup questions 24/7', 'Track onboarding progress', 'Send automated reminders'],
    cannotDo: ['Replace personalised human onboarding for enterprise clients', 'Handle highly custom configurations', 'Guarantee user adoption'],
  },
  'ai-data-analyst': {
    canDo: ['Surface insights from business data', 'Generate reports automatically', 'Track KPIs and trends', 'Alert on anomalies'],
    cannotDo: ['Replace human strategic thinking', 'Make business decisions autonomously', 'Guarantee data accuracy from external sources'],
  },
  'ai-hr-assistant': {
    canDo: ['Answer routine HR policy questions', 'Process leave requests', 'Assist with recruitment screening', 'Handle onboarding paperwork'],
    cannotDo: ['Make hiring or firing decisions', 'Handle grievances or disciplinary matters', 'Replace qualified HR professionals for sensitive matters'],
  },
};
const STEPS = [
  'Your biggest challenge',
  'Your industry',
  'Daily interactions',
  'Current setup',
  'Budget',
];
export function AgentFinderClient() {
  const [step, setStep] = useState(0);
  const [challenge, setChallenge] = useState('');
  const [industry, setIndustry] = useState('');
  const [volume, setVolume] = useState('');
  const [currentSetup, setCurrentSetup] = useState('');
  const [budget, setBudget] = useState('');
  const [results, setResults] = useState<AgentMatch[] | null>(null);
  const challenges = [
    { value: 'missed-calls', label: 'Missed calls and enquiries' },
    { value: 'slow-responses', label: 'Slow response times' },
    { value: 'booking-management', label: 'Booking and scheduling headaches' },
    { value: 'lead-generation', label: 'Not enough leads or follow-up' },
    { value: 'social-media', label: 'Managing social media presence' },
    { value: 'review-management', label: 'Keeping on top of online reviews' },
    { value: 'invoicing', label: 'Invoicing and payment chasing' },
    { value: 'email-overload', label: 'Email overload' },
    { value: 'onboarding', label: 'Customer onboarding takes too long' },
    { value: 'data-analysis', label: 'No time for data analysis' },
    { value: 'hr-admin', label: 'HR admin is overwhelming' },
  ];
  const calculateResults = () => {
    const mapping = CHALLENGE_TO_AGENTS[challenge] || { slugs: ['ai-receptionist', 'customer-support'], reason: 'Based on your challenge type' };
    const matches: AgentMatch[] = mapping.slugs.map((slug, i) => {
      const agent = agentTypes.find((a) => a.slug === slug);
      const caps = AGENT_CAPABILITIES[slug] || { canDo: ['Handle routine tasks', 'Work 24/7', 'Integrate with existing tools'], cannotDo: ['Replace human judgement', 'Handle every scenario'] };
      return {
        slug,
        name: agent?.name || slug,
        matchScore: Math.max(95 - i * 12, 65),
        reason: mapping.reason,
        canDo: caps.canDo,
        cannotDo: caps.cannotDo,
      };
    });
    setResults(matches);
  };
  const handleNext = () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      calculateResults();
    }
  };
  const canProceed = () => {
    if (step === 0) return challenge !== '';
    if (step === 1) return industry !== '';
    if (step === 2) return volume !== '';
    if (step === 3) return currentSetup !== '';
    if (step === 4) return budget !== '';
    return false;
  };
  // Results view
  if (results) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20">
        <h1 className="text-3xl font-bold text-white font-[family-name:var(--font-outfit)] mb-8">
          Your Recommended AI Agents
        </h1>
        <div className="space-y-6">
          {results.map((match, i) => (
            <div key={match.slug} className="p-6 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">#{i + 1} Match</span>
                  <h2 className="text-xl font-semibold text-white font-[family-name:var(--font-outfit)]">
                    {match.name}
                  </h2>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold font-[family-name:var(--font-outfit)]" style={{ color: match.matchScore >= 85 ? '#22c55e' : '#4361ee' }}>
                    {match.matchScore}%
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">match</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-4">{match.reason}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-green-400 text-sm font-medium mb-2">✓ What it can do</h3>
                  <ul className="space-y-1">
                    {match.canDo.map((item) => (
                      <li key={item} className="text-gray-400 text-xs">• {item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-amber-400 text-sm font-medium mb-2">⚠ Limitations</h3>
                  <ul className="space-y-1">
                    {match.cannotDo.map((item) => (
                      <li key={item} className="text-gray-500 text-xs">• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="mt-4 flex gap-3">
                {industry && (
                  <Link href={`/solutions/${match.slug}/${industry}`} className="text-sm text-[#4361ee] hover:underline">
                    See for your industry →
                  </Link>
                )}
                {industry && (
                  <Link href={`/guides/${industry}`} className="text-sm text-gray-500 hover:underline">
                    Read full guide →
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 p-4 rounded-lg bg-amber-500/[0.05] border border-amber-500/[0.15]">
          <p className="text-amber-200/70 text-sm">
            These recommendations are based on your responses and general industry patterns.
            The best way to confirm fit is to try a specific agent for your use case.
            We offer month-to-month plans with no long-term commitment.
          </p>
        </div>
        <div className="mt-6 text-center">
          <button onClick={() => { setStep(0); setChallenge(''); setIndustry(''); setVolume(''); setCurrentSetup(''); setBudget(''); setResults(null); }} className="text-[#4361ee] hover:underline text-sm">
            Start over
          </button>
        </div>
      </div>
    );
  }
  // Step view
  const renderOptions = (options: { value: string; label: string }[], selected: string, onSelect: (v: string) => void) => (
    <div className="space-y-3">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          className={`w-full text-left p-4 rounded-lg border transition-all ${
            selected === opt.value
              ? 'bg-[#4361ee]/10 border-[#4361ee]/40 text-white'
              : 'bg-white/[0.03] border-white/[0.06] text-gray-300 hover:border-white/[0.15] hover:bg-white/[0.05]'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
  return (
    <div className="max-w-2xl mx-auto px-6 py-20">
      <nav className="text-sm text-gray-500 dark:text-gray-400 mb-8" aria-label="Breadcrumb">
        <Link href="/" className="hover:text-white transition-colors">Home</Link>
        <span className="mx-2">/</span>
        <Link href="/tools" className="hover:text-white transition-colors">Tools</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-300">Agent Finder</span>
      </nav>
      <h1 className="text-3xl font-bold text-white font-[family-name:var(--font-outfit)] mb-2">
        Find the Right AI Agent
      </h1>
      <p className="text-gray-400 mb-8">5 questions. Honest recommendations with clear limitations.</p>
      {/* Progress */}
      <div className="flex gap-2 mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1.5 rounded-full ${i <= step ? 'bg-gradient-to-r from-[#4361ee] to-[#7c3aed]' : 'bg-white/[0.06]'}`} />
            <p className={`text-xs mt-1 ${i === step ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`}>{s}</p>
          </div>
        ))}
      </div>
      {step === 0 && (
        <>
          <h2 className="text-xl text-white font-medium font-[family-name:var(--font-outfit)] mb-4">What is your biggest challenge?</h2>
          {renderOptions(challenges, challenge, setChallenge)}
        </>
      )}
      {step === 1 && (
        <>
          <h2 className="text-xl text-white font-medium font-[family-name:var(--font-outfit)] mb-4">What industry are you in?</h2>
          {renderOptions(industries.map((i) => ({ value: i.slug, label: i.name })), industry, setIndustry)}
        </>
      )}
      {step === 2 && (
        <>
          <h2 className="text-xl text-white font-medium font-[family-name:var(--font-outfit)] mb-4">How many customer interactions per day?</h2>
          {renderOptions([
            { value: '<5', label: 'Fewer than 5' },
            { value: '5-20', label: '5-20' },
            { value: '20-50', label: '20-50' },
            { value: '50+', label: 'More than 50' },
          ], volume, setVolume)}
        </>
      )}
      {step === 3 && (
        <>
          <h2 className="text-xl text-white font-medium font-[family-name:var(--font-outfit)] mb-4">What is your current setup?</h2>
          {renderOptions([
            { value: 'nothing', label: 'No digital customer service tools' },
            { value: 'basic', label: 'Basic contact form / email only' },
            { value: 'phone', label: 'Phone and email' },
            { value: 'chat', label: 'Live chat on website' },
            { value: 'crm', label: 'CRM with support ticketing' },
          ], currentSetup, setCurrentSetup)}
        </>
      )}
      {step === 4 && (
        <>
          <h2 className="text-xl text-white font-medium font-[family-name:var(--font-outfit)] mb-4">What is your monthly budget?</h2>
          {renderOptions([
            { value: '<100', label: 'Under $100/month' },
            { value: '100-300', label: '$100-$300/month' },
            { value: '300-500', label: '$300-$500/month' },
            { value: '500+', label: 'Over $500/month' },
          ], budget, setBudget)}
        </>
      )}
      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <button
          onClick={() => setStep(Math.max(0, step - 1))}
          className={`px-6 py-3 rounded-lg border border-white/[0.15] text-white hover:bg-white/[0.05] transition-colors ${step === 0 ? 'opacity-0 pointer-events-none' : ''}`}
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!canProceed()}
          className="px-8 py-3 rounded-lg bg-gradient-to-r from-[#4361ee] to-[#7c3aed] text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {step === 4 ? 'See My Results' : 'Next'}
        </button>
      </div>
    </div>
  );
}
